(() => {
  if (customElements.get('lasgym-paypal-product')) return;

  class LasgymPayPalProduct extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.root = this.closest('section-product');
      this.form = document.getElementById(this.dataset.formId);
      this.trigger = this.querySelector('[data-lg-paypal-open]');
      this.status = this.querySelector('[data-lg-paypal-status]');
      this.errorLink = this.querySelector('[data-lg-paypal-error-link]');
      this.dialog = this.querySelector('dialog');
      this.content = this.dialog?.querySelector('[data-lg-paypal-content]');
      this.dialogStatus = this.dialog?.querySelector('[data-lg-paypal-dialog-status]');
      if (!this.root || !this.form || !this.dialog) return;
      this.cacheKey = 'lasgym-paypal-intent:' + this.dataset.productId;
      this.busy = false;
      this.variantPending = false;
      this.ambiguous = false;
      this.generation = 0;
      this.prepared = this.restore();
      const options = {signal: this.controller.signal};
      this.backdrop = document.createElement('div');
      this.backdrop.className = 'LGPayPalBackdrop';
      this.backdrop.hidden = true;
      this.backdrop.setAttribute('aria-hidden', 'true');
      document.body.append(this.backdrop, this.dialog);
      this.addEventListener('click', event => {
        const button = event.target instanceof Element ? event.target.closest('[data-lg-paypal-open]') : null;
        if (!button || button.closest('lasgym-paypal-product') !== this) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this.trigger = button;
        if (!button.disabled) this.open();
      }, {capture: true, signal: this.controller.signal});
      this.backdrop.addEventListener('click', () => this.dialog.close(), options);
      this.backdrop.addEventListener('wheel', event => event.preventDefault(), {passive: false, signal: this.controller.signal});
      this.dialog.querySelectorAll('[data-lg-paypal-close]').forEach(button => button.addEventListener('click', () => this.dialog.close(), options));
      this.dialog.addEventListener('close', () => {
        this.generation += 1;
        this.walletObserver?.disconnect();
        clearTimeout(this.walletTimer);
        this.stopHandoffWatch();
        this.backdrop.hidden = true;
        this.handoff = false;
        this.dialog.removeAttribute('aria-modal');
        this.trigger.focus({preventScroll: true});
      }, options);
      this.content.addEventListener('click', event => {
        if (!(event.target instanceof Element) || !event.target.closest('shopify-paypal-button')) return;
        this.watchNativeHandoff();
      }, options);
      this.dialog.addEventListener('keydown', event => {
        if (this.handoff) return;
        if (event.key === 'Escape') {
          event.preventDefault();
          this.dialog.close();
        } else if (event.key === 'Tab') {
          const controls = this.focusableControls();
          const first = controls[0], last = controls[controls.length - 1];
          if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last?.focus();
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first?.focus();
          }
        }
      }, options);
      this.root.addEventListener('change', event => {
        if (!this.isProductControl(event.target)) return;
        this.forget();
        if (event.target.closest('.ProductForm__Option')) this.variantPending = true;
        this.sync();
      }, options);
      this.root.addEventListener('input', event => {
        if (!this.isProductControl(event.target)) return;
        this.forget();
        if (event.target.matches('[name="properties[Nome]"], [name="properties[Numero]"]')) event.target.setCustomValidity('');
      }, options);
      this.root.addEventListener('variant:changed', () => {
        this.variantPending = false;
        this.sync();
      }, options);
      document.addEventListener('product:added', () => {
        if (!this.busy) this.forget();
      }, options);
      window.addEventListener('pageshow', () => {
        this.busy = false;
        this.sync();
      }, options);
      this.observer = new MutationObserver(() => this.sync());
      this.observer.observe(this.root, {subtree: true, childList: true, attributes: true, attributeFilter: ['disabled']});
      customElements.whenDefined('section-product').then(() => this.sync());
      this.sync();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      this.walletObserver?.disconnect();
      clearTimeout(this.walletTimer);
      this.stopHandoffWatch();
      this.cancelAdd?.();
      this.dialog?.remove();
      this.backdrop?.remove();
      this.controller = null;
    }

    focusableControls() {
      return [...this.dialog.querySelectorAll('button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), iframe, [tabindex]:not([tabindex="-1"])')].filter(element => element.getClientRects().length && getComputedStyle(element).visibility !== 'hidden');
    }

    isProductControl(target) {
      return target instanceof HTMLElement && (target.form === this.form || Boolean(target.closest('.ProductForm__Option')));
    }

    sync() {
      this.trigger = this.querySelector('[data-lg-paypal-open]');
      if (!this.trigger || !this.form) return;
      const nativeButton = this.root.querySelector('.ProductForm__AddToCart');
      const disabled = this.busy || this.variantPending || !nativeButton || nativeButton.disabled;
      if (this.trigger.disabled !== disabled) this.trigger.disabled = disabled;
      this.trigger.setAttribute('aria-busy', String(this.busy));
    }

    restore() {
      try {
        const stored = JSON.parse(sessionStorage.getItem(this.cacheKey) || 'null');
        if (stored && Date.now() - stored.created < 30 * 60 * 1000) return stored;
      } catch {}
      return null;
    }

    remember(record) {
      this.prepared = record;
      try { sessionStorage.setItem(this.cacheKey, JSON.stringify(record)); } catch {}
    }

    forget() {
      this.prepared = null;
      try { sessionStorage.removeItem(this.cacheKey); } catch {}
    }

    message(text, showCart = false) {
      this.status.textContent = text || '';
      this.errorLink.hidden = !showCart;
    }

    async readCart() {
      const response = await fetch(this.dataset.cartUrl + '.js', {credentials: 'same-origin', cache: 'no-store', headers: {'Accept': 'application/json'}});
      if (!response.ok) throw new Error(this.dataset.errorText);
      return response.json();
    }

    async snapshot() {
      const data = new FormData(this.form);
      const id = String(data.get('id') || '');
      const quantity = Number(data.get('quantity') || 1);
      const variant = this.root.productVariants?.currentVariant;
      if (!id || !Number.isInteger(quantity) || quantity < 1 || !variant?.available || String(variant.id) !== id || this.variantPending) throw new Error(this.dataset.optionsText);
      const entries = [...data.entries()].filter(([key]) => key === 'id' || key === 'quantity' || key === 'selling_plan' || key.startsWith('properties['));
      const normalized = entries.map(([key, value]) => [key, value instanceof File ? [value.name, value.size, value.lastModified] : value]);
      normalized.sort((a, b) => a[0].localeCompare(b[0]));
      const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(JSON.stringify(normalized)));
      const signature = [...new Uint8Array(digest)].map(value => value.toString(16).padStart(2, '0')).join('');
      return {id, quantity, signature, properties: Object.fromEntries(entries.filter(([key]) => key.startsWith('properties[')).map(([key, value]) => [key.slice(11, -1), String(value)])), sellingPlan: String(data.get('selling_plan') || '')};
    }

    validate() {
      if (this.dataset.personalizable === 'true') {
        for (const name of ['properties[Nome]', 'properties[Numero]']) {
          const input = this.form.elements.namedItem(name);
          if (input && typeof input.setCustomValidity === 'function') input.setCustomValidity(input.value.trim() ? '' : this.dataset.optionsText);
        }
      }
      return this.form.reportValidity();
    }

    matchingLine(cart, snapshot) {
      return cart.items?.find(item => {
        if (String(item.variant_id) !== snapshot.id) return false;
        if (String(item.selling_plan_allocation?.selling_plan?.id || '') !== snapshot.sellingPlan) return false;
        const properties = Object.fromEntries(Object.entries(item.properties || {}).filter(([, value]) => value !== '' && value != null));
        const expected = Object.fromEntries(Object.entries(snapshot.properties).filter(([, value]) => value !== ''));
        return Object.keys(properties).length === Object.keys(expected).length && Object.entries(expected).every(([key, value]) => String(properties[key] || '') === value);
      });
    }

    reusable(cart, snapshot) {
      const saved = this.prepared;
      if (!saved || saved.signature !== snapshot.signature || saved.cartToken !== cart.token) return false;
      const line = cart.items?.find(item => item.key === saved.lineKey);
      return Boolean(line && line.quantity >= saved.quantity);
    }

    requestNativeAdd() {
      return new Promise((resolve, reject) => {
        if (this.root.lgPayPalIntent) {
          reject(new Error(this.dataset.waitingText));
          return;
        }
        let finished = false;
        let started = false;
        let timer;
        const finish = (error, result) => {
          if (finished) return;
          finished = true;
          clearTimeout(timer);
          if (this.root.lgPayPalIntent === intent) delete this.root.lgPayPalIntent;
          this.cancelAdd = null;
          if (error) reject(error); else resolve(result);
        };
        const intent = {
          started: () => {
            started = true;
            clearTimeout(timer);
            timer = setTimeout(() => intent.failure(this.dataset.uncertainText, {uncertain: true}), 60000);
          },
          success: result => finish(null, result),
          failure: (message, details = {}) => {
            const error = new Error(typeof message === 'string' && message ? message : this.dataset.errorText);
            error.uncertain = Boolean(details.uncertain);
            finish(error);
          }
        };
        this.root.lgPayPalIntent = intent;
        this.cancelAdd = () => intent.failure(this.dataset.errorText, {uncertain: started});
        timer = setTimeout(() => intent.failure(this.dataset.optionsText, {uncertain: false}), 4000);
        const button = this.root.querySelector('.ProductForm__AddToCart');
        if (!button || button.disabled) intent.failure(this.dataset.unavailableText);
        else button.click();
      });
    }

    async open() {
      if (this.busy || this.variantPending || !this.validate()) return;
      if (this.ambiguous) {
        this.message(this.dataset.uncertainText, true);
        return;
      }
      this.busy = true;
      this.sync();
      this.message(this.dataset.loadingText);
      let snapshot;
      let before;
      let added;
      try {
        snapshot = await this.snapshot();
        before = await this.readCart();
        const latest = await this.snapshot();
        if (snapshot.signature !== latest.signature) throw new Error(this.dataset.optionsText);
        if (!this.reusable(before, snapshot)) {
          this.forget();
          added = await this.requestNativeAdd();
          const resultLine = added?.items?.find(item => String(item.variant_id || item.id) === snapshot.id) || added;
          if (resultLine?.key && Number(resultLine.quantity) > 0) {
            this.remember({signature: snapshot.signature, cartToken: before.token, lineKey: resultLine.key, quantity: Number(resultLine.quantity), created: Date.now()});
          } else {
            this.ambiguous = true;
          }
        }
        const cart = await this.readCart();
        const addedItem = added?.items?.[0] || added;
        const line = cart.items?.find(item => addedItem?.key && item.key === addedItem.key) || this.matchingLine(cart, snapshot);
        if (!line) {
          this.ambiguous = true;
          throw new Error(this.dataset.uncertainText);
        }
        this.remember({signature: snapshot.signature, cartToken: cart.token, lineKey: line.key, quantity: line.quantity, created: Date.now()});
        this.ambiguous = false;
        this.message(this.dataset.readyText);
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {bubbles: true}));
        await this.showSummary();
      } catch (error) {
        if (error.uncertain && snapshot && before) {
          this.ambiguous = true;
          try {
            const current = await this.readCart();
            const priorLine = this.matchingLine(before, snapshot);
            const currentLine = this.matchingLine(current, snapshot);
            if (currentLine && currentLine.quantity >= (priorLine?.quantity || 0) + snapshot.quantity) {
              this.remember({signature: snapshot.signature, cartToken: current.token, lineKey: currentLine.key, quantity: currentLine.quantity, created: Date.now()});
              this.ambiguous = false;
              this.message(this.dataset.readyText);
              document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {bubbles: true}));
              await this.showSummary();
              return;
            }
          } catch {}
        }
        const errorText = error.uncertain || this.ambiguous ? this.dataset.uncertainText : error.message || this.dataset.errorText;
        this.message(errorText, true);
        if (this.dialog.open) this.dialogStatus.textContent = errorText;
      } finally {
        this.busy = false;
        this.sync();
      }
    }

    async showSummary() {
      const generation = ++this.generation;
      this.handoff = false;
      this.stopHandoffWatch();
      this.walletObserver?.disconnect();
      clearTimeout(this.walletTimer);
      this.content.replaceChildren();
      this.dialogStatus.textContent = this.dataset.loadingText;
      this.backdrop.hidden = false;
      this.dialog.setAttribute('aria-modal', 'true');
      if (!this.dialog.open) this.dialog.show();
      this.focusableControls()[0]?.focus({preventScroll: true});
      const url = new URL(this.dataset.cartUrl, window.location.origin);
      url.searchParams.set('section_id', 'lasgym-paypal-cart');
      const response = await fetch(url, {credentials: 'same-origin', cache: 'no-store'});
      if (!response.ok) throw new Error(this.dataset.errorText);
      const html = await response.text();
      if (generation !== this.generation || !this.dialog.open) return;
      const documentFragment = new DOMParser().parseFromString(html, 'text/html');
      const summary = documentFragment.querySelector('[data-lg-paypal-cart]');
      if (!summary || Number(summary.dataset.itemCount) < 1) throw new Error(this.dataset.errorText);
      this.content.replaceChildren(summary);
      window.Shopify?.PaymentButton?.init();
      this.watchWallet();
    }

    stopHandoffWatch() {
      this.handoffObserver?.disconnect();
      this.handoffObserver = null;
      clearTimeout(this.handoffTimer);
      clearInterval(this.handoffPoll);
    }

    watchNativeHandoff() {
      this.stopHandoffWatch();
      this.handoff = true;
      this.dialog.setAttribute('aria-modal', 'false');
      let seenOverlay = false;
      let popupAway = false;
      const visibleOverlay = () => [...document.querySelectorAll('iframe[title]')].find(frame => {
        if (frame.title.trim().toLowerCase() !== 'paypal checkout overlay') return false;
        const box = frame.getBoundingClientRect();
        if (!box.width || !box.height) return false;
        for (let element = frame; element && element !== document.documentElement; element = element.parentElement) {
          const style = getComputedStyle(element);
          if (style.display === 'none' || style.visibility === 'hidden' || Number(style.opacity) === 0) return false;
        }
        return true;
      });
      const resumeSummary = () => {
        this.stopHandoffWatch();
        this.handoff = false;
        if (this.dialog.open) this.dialog.setAttribute('aria-modal', 'true');
      };
      const update = () => {
        if (!this.dialog.open) { this.stopHandoffWatch(); return; }
        if (visibleOverlay()) { seenOverlay = true; return; }
        if (seenOverlay) { resumeSummary(); return; }
        if (!document.hasFocus()) popupAway = true;
        else if (popupAway) resumeSummary();
      };
      this.handoffObserver = new MutationObserver(update);
      this.handoffObserver.observe(document.body, {subtree: true, childList: true, attributes: true, attributeFilter: ['style', 'class', 'hidden', 'title']});
      this.handoffPoll = setInterval(update, 100);
      this.handoffTimer = setTimeout(() => {
        clearInterval(this.handoffPoll);
        if (!visibleOverlay() && document.hasFocus()) resumeSummary();
      }, 60000);
      update();
    }

    watchWallet() {
      const update = () => {
        const wallet = this.content.querySelector('shopify-paypal-button');
        const frame = wallet?.querySelector('iframe');
        if (!wallet || !frame || !frame.getBoundingClientRect().height) return false;
        this.dialogStatus.textContent = '';
        clearTimeout(this.walletTimer);
        this.walletObserver?.disconnect();
        return true;
      };
      this.dialogStatus.textContent = this.dataset.waitingText;
      if (update()) return;
      this.walletObserver = new MutationObserver(update);
      this.walletObserver.observe(this.content, {subtree: true, childList: true, attributes: true, attributeFilter: ['style', 'class', 'hidden']});
      this.walletTimer = setTimeout(() => {
        if (!update()) this.dialogStatus.textContent = this.dataset.missingText;
      }, 12000);
    }
  }

  customElements.define('lasgym-paypal-product', LasgymPayPalProduct);
})();
